import { alpha, Box, IconButton, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { Pagination, PaginationResponse } from '@ekkogmbh/apisdk';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { DragIndicator, RemoveCircle } from '@mui/icons-material';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { arrayMoveMutable } from 'array-move';
import useEkko from 'src/Common/Providers/EkkoProvider';
import PaginatedSelect from 'src/Common/Components/Forms/PaginatedSelect';
import { CenteredColumn } from 'src/Common/Components/Stacks';

interface Props {
  coordinates: string[];
  onChange: (coordinates: string[]) => void;
}

const SortableItem = SortableElement(({ value, sortIndex }: { value: string; sortIndex: number }) => (
  <ListItem
    sx={(theme) => ({
      borderTopStyle: 'inset',
      borderTopColor: alpha(theme.palette.text.primary, 0.23),
      borderTopWidth: 1,
      borderRightStyle: 'inset',
      borderRightColor: alpha(theme.palette.text.primary, 0.23),
      borderRightWidth: 2,
      backgroundColor: 'background.paper',
      userSelect: 'none',
    })}
  >
    <ListItemIcon>
      <DragIndicator color={'primary'} />
    </ListItemIcon>
    <ListItemText>
      <Typography variant={'subtitle1'} color={'secondary'}>
        <span style={{ display: 'inline-block', minWidth: 40 }}>{sortIndex + 1}.&nbsp;</span>
        <span style={{ display: 'inline-block', minWidth: 160 }}>{value}</span>
      </Typography>
    </ListItemText>
  </ListItem>
));

const SortableList = SortableContainer(
  ({ items, onRemove }: { items: string[]; onRemove: (value: string, sortIndex?: number) => void }) => (
    <Stack direction={'row'}>
      <List style={{ padding: 4, width: '100%' }}>
        {items.map((value: string, index: number) => (
          <SortableItem key={`sortable-item-comp-${index}`} index={index} sortIndex={index} value={value} />
        ))}
      </List>
      <List disablePadding dense>
        {items.map((value: string, index: number) => (
          <ListItem key={index}>
            <IconButton size={'large'} onClick={() => onRemove(value, index)}>
              <RemoveCircle />
            </IconButton>
          </ListItem>
        ))}
      </List>
    </Stack>
  ),
);

const paperWrap = (content: React.JSX.Element): React.JSX.Element => (
  <Box
    sx={(theme) => ({
      borderStyle: 'solid',
      borderRadius: 0.5,
      borderWidth: 1,
      borderColor: alpha(theme.palette.text.primary, 0.23),
    })}
  >
    {content}
  </Box>
);

export const DevicePoolSorting = ({ coordinates, onChange }: Props): React.JSX.Element => {
  const { api } = useEkko();

  const fetchCompartmentCoordinates = useCallback(
    async (pagination: Pagination) => {
      const response = await api.getCompartments(pagination);
      return {
        ...response,
        items: (response.items ?? []).map((c) => c.coordinate),
      } as PaginationResponse<string>;
    },
    [api],
  );

  return (
    <CenteredColumn width={'100%'}>
      <PaginatedSelect
        multiple
        multipleVariant={'count'}
        value={coordinates}
        label={'Coordinates'}
        itemId={(c: string) => c}
        itemRender={(c: string) => c}
        fetchPaginatedItems={fetchCompartmentCoordinates}
        onChange={(coords) => onChange(coords as string[])}
      />
      {paperWrap(
        <Stack direction={'column'}>
          <StyledFormHeader label={'Sorted Coordinates'} />
          {coordinates.length < 1 && (
            <Typography color={'secondary'} variant={'subtitle2'} align={'center'}>
              select coordinates to add them for sorting
            </Typography>
          )}
          {coordinates.length > 0 && (
            <SortableList
              lockAxis={'y'}
              items={coordinates}
              onSortEnd={({ oldIndex, newIndex }) => {
                arrayMoveMutable(coordinates, oldIndex, newIndex);
                onChange(coordinates);
              }}
              onRemove={(_, idx) => {
                if (idx === undefined) {
                  return;
                }
                coordinates.splice(idx, 1);
                onChange(coordinates);
              }}
            />
          )}
        </Stack>,
      )}
    </CenteredColumn>
  );
};
